import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Type, AlignLeft, ChevronDown, CircleDot, CheckSquare,
  GripVertical, Trash2, Plus, Settings, Eye,
  FileText, Save,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import CommonToaster from '../Common/CommonToaster';
import { PageHeader, BackButton, AppButton } from '../ui';
import './enquiry-forms.scss';

const FIELD_TYPES = [
  { type: 'text', label: 'Text', icon: Type },
  { type: 'textarea', label: 'Textarea', icon: AlignLeft },
  { type: 'dropdown', label: 'Dropdown', icon: ChevronDown },
  { type: 'radio', label: 'Radio', icon: CircleDot },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const FormBuilder = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  const [formName, setFormName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [settings, setSettings] = useState({
    submitButtonText: 'Submit',
    successMessage: 'Thank you! We will get back to you soon.',
    theme: 'default',
    redirectUrl: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Load default fields for new form
  const loadDefaults = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/enquiry-forms/default-fields');
      setFields(data.fields || []);
    } catch {
      // Fallback defaults
      setFields([
        { fieldId: 'name', type: 'text', label: 'Full Name', placeholder: 'Enter your full name', required: true, isDefault: true, defaultField: 'name', options: [], order: 0 },
        { fieldId: 'email', type: 'email', label: 'Email Address', placeholder: 'Enter your email', required: true, isDefault: true, defaultField: 'email', options: [], order: 1 },
        { fieldId: 'phone', type: 'phone', label: 'Phone Number', placeholder: 'Enter your phone number', required: true, isDefault: true, defaultField: 'phone', options: [], order: 2 },
        { fieldId: 'course', type: 'dropdown', label: 'Course', placeholder: 'Select a course', required: false, isDefault: true, defaultField: 'course', options: [], order: 3 },
        { fieldId: 'message', type: 'textarea', label: 'Message', placeholder: 'Enter your message', required: false, isDefault: true, defaultField: 'message', options: [], order: 4 },
      ]);
    }
  }, []);

  // Load existing form for edit
  const loadForm = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/enquiry-forms/${editId}`);
      const f = data.form;
      setFormName(f.name);
      setDescription(f.description || '');
      setFields(f.fields || []);
      if (f.settings) setSettings(prev => ({ ...prev, ...f.settings }));
    } catch {
      setToast({ show: true, message: 'Failed to load form.', type: 'error' });
      navigate('/enquiry-forms');
    }
    setLoading(false);
  }, [editId, navigate]);

  useEffect(() => {
    if (isEdit) {
      loadForm();
    } else {
      loadDefaults();
    }
  }, [isEdit, loadForm, loadDefaults]);

  // ─── Field operations ──────────────────────────────────────────────────

  const updateField = (index, updates) => {
    setFields(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  const removeField = (index) => {
    setFields(prev => prev.filter((_, i) => i !== index));
  };

  const addField = (type) => {
    const newField = {
      fieldId: generateFieldId(),
      type,
      label: `${type.charAt(0).toUpperCase() + type.slice(1)} Field`,
      placeholder: '',
      required: false,
      isDefault: false,
      defaultField: null,
      options: ['dropdown', 'radio', 'checkbox'].includes(type) ? ['Option 1'] : [],
      order: fields.length,
    };
    setFields(prev => [...prev, newField]);
  };

  const moveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const updated = [...fields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFields(updated.map((f, i) => ({ ...f, order: i })));
  };

  // ─── Options management ────────────────────────────────────────────────

  const addOption = (fieldIndex) => {
    const field = fields[fieldIndex];
    const newOptions = [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`];
    updateField(fieldIndex, { options: newOptions });
  };

  const updateOption = (fieldIndex, optIndex, value) => {
    const field = fields[fieldIndex];
    const newOptions = [...(field.options || [])];
    newOptions[optIndex] = value;
    updateField(fieldIndex, { options: newOptions });
  };

  const removeOption = (fieldIndex, optIndex) => {
    const field = fields[fieldIndex];
    const newOptions = (field.options || []).filter((_, i) => i !== optIndex);
    updateField(fieldIndex, { options: newOptions });
  };

  // ─── Save ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!formName.trim()) {
      setToast({ show: true, message: 'Form name is required.', type: 'error' });
      return;
    }
    if (fields.length === 0) {
      setToast({ show: true, message: 'Add at least one field.', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formName.trim(),
        description: description.trim(),
        fields: fields.map((f, i) => ({ ...f, order: i })),
        settings,
      };

      if (isEdit) {
        await apiClient.put(`/enquiry-forms/${editId}`, payload);
        setToast({ show: true, message: 'Form updated successfully.', type: 'success' });
      } else {
        await apiClient.post('/enquiry-forms', payload);
        setToast({ show: true, message: 'Form created successfully.', type: 'success' });
      }

      setTimeout(() => navigate('/enquiry-forms'), 1000);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to save form.', type: 'error' });
    }
    setSaving(false);
  };

  // ─── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="efb-page">
        <PageHeader icon={FileText} title="Loading..." />
        <div className="text-center py-5">Loading form...</div>
      </div>
    );
  }

  return (
    <div className="efb-page">
      <CommonToaster toast={toast} setToast={setToast} />

      <PageHeader
        icon={FileText}
        title={isEdit ? 'Edit Enquiry Form' : 'Create Enquiry Form'}
        description="Configure fields, settings, and preview your form"
        actions={
          <div className="d-flex gap-2">
            <AppButton variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
              <Eye size={16} /> {showPreview ? 'Hide Preview' : 'Preview'}
            </AppButton>
          </div>
        }
      />
      <BackButton />

      {/* Form Details */}
      <div className="efb-section">
        <div className="efb-section-title"><FileText size={16} /> Form Details</div>
        <div className="efb-row">
          <div className="efb-field">
            <label className="efb-label">Form Name *</label>
            <input
              className="efb-input"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Website Enquiry Form"
            />
          </div>
          <div className="efb-field">
            <label className="efb-label">Description (optional)</label>
            <input
              className="efb-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this form"
            />
          </div>
        </div>
      </div>

      {/* Fields Section */}
      <div className="efb-section">
        <div className="efb-section-title"><Settings size={16} /> Form Fields</div>

        <div className="efb-fields-list">
          {fields.map((field, idx) => (
            <div key={field.fieldId} className={`efb-field-card ${field.isDefault ? 'efb-field-default' : ''}`}>
              <div className="efb-field-header">
                <div className="efb-field-header-left">
                  <span className="efb-field-grip" onClick={() => moveField(idx, -1)} title="Move up">
                    <GripVertical size={16} />
                  </span>
                  <span className="efb-field-type-badge">{field.type}</span>
                  {field.isDefault && <span className="efb-field-type-badge">default</span>}
                </div>
                <div className="efb-field-header-actions">
                  <label className="efb-field-toggle">
                    Required
                    <input
                      type="checkbox"
                      className="efb-toggle-switch"
                      checked={field.required}
                      onChange={(e) => updateField(idx, { required: e.target.checked })}
                    />
                  </label>
                  {!field.isDefault && (
                    <button className="efb-field-remove" onClick={() => removeField(idx)} title="Remove field">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="efb-field-body">
                <div>
                  <label className="efb-label">Label</label>
                  <input
                    className="efb-input"
                    value={field.label}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                    placeholder="Field label"
                  />
                </div>
                <div>
                  <label className="efb-label">Placeholder</label>
                  <input
                    className="efb-input"
                    value={field.placeholder || ''}
                    onChange={(e) => updateField(idx, { placeholder: e.target.value })}
                    placeholder="Placeholder text"
                  />
                </div>

                {!field.isDefault && (
                  <div>
                    <label className="efb-label">Field Type</label>
                    <select
                      className="efb-input"
                      value={field.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        const needsOptions = ['dropdown', 'radio', 'checkbox'].includes(newType);
                        updateField(idx, {
                          type: newType,
                          options: needsOptions && (!field.options || field.options.length === 0) ? ['Option 1'] : field.options,
                        });
                      }}
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="radio">Radio</option>
                      <option value="checkbox">Checkbox</option>
                    </select>
                  </div>
                )}

                {/* Courses API toggle for dropdown fields */}
                {field.type === 'dropdown' && !field.isDefault && (
                  <div className="efb-field-body-full">
                    <label className="efb-field-toggle">
                      Auto-populate with your courses
                      <input
                        type="checkbox"
                        className="efb-toggle-switch"
                        checked={field.defaultField === 'course'}
                        onChange={(e) => updateField(idx, {
                          defaultField: e.target.checked ? 'course' : null,
                          options: e.target.checked ? [] : (field.options?.length ? field.options : ['Option 1']),
                        })}
                      />
                    </label>
                    {field.defaultField === 'course' && (
                      <p className="efb-course-api-note">Courses will be loaded automatically from your institute's course list when the form is displayed.</p>
                    )}
                  </div>
                )}

                {/* Options for dropdown/radio/checkbox */}
                {['dropdown', 'radio', 'checkbox'].includes(field.type) && !field.isDefault && field.defaultField !== 'course' && (
                  <div className="efb-field-body-full">
                    <label className="efb-label">Options</label>
                    <div className="efb-options-list">
                      {(field.options || []).map((opt, optIdx) => (
                        <div key={optIdx} className="efb-option-row">
                          <input
                            className="efb-option-input"
                            value={opt}
                            onChange={(e) => updateOption(idx, optIdx, e.target.value)}
                            placeholder={`Option ${optIdx + 1}`}
                          />
                          <button className="efb-option-remove" onClick={() => removeOption(idx, optIdx)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button className="efb-option-add" onClick={() => addOption(idx)}>
                        <Plus size={14} /> Add option
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add field buttons */}
        <div className="efb-add-field-bar">
          {FIELD_TYPES.map((ft) => (
            <button key={ft.type} className="efb-add-field-btn" onClick={() => addField(ft.type)}>
              <ft.icon size={14} /> {ft.label}
            </button>
          ))}
        </div>
      </div>

      {/* Settings */}
      <div className="efb-section">
        <div className="efb-section-title"><Settings size={16} /> Form Settings</div>
        <div className="efb-settings-grid">
          <div className="efb-field">
            <label className="efb-label">Submit Button Text</label>
            <input
              className="efb-input"
              value={settings.submitButtonText}
              onChange={(e) => setSettings(prev => ({ ...prev, submitButtonText: e.target.value }))}
              placeholder="Submit"
            />
          </div>
          <div className="efb-field">
            <label className="efb-label">Theme</label>
            <select
              className="efb-input"
              value={settings.theme}
              onChange={(e) => setSettings(prev => ({ ...prev, theme: e.target.value }))}
            >
              <option value="default">Default</option>
              <option value="minimal">Minimal</option>
              <option value="modern">Modern</option>
            </select>
          </div>
          <div className="efb-field" style={{ gridColumn: '1 / -1' }}>
            <label className="efb-label">Success Message</label>
            <input
              className="efb-input"
              value={settings.successMessage}
              onChange={(e) => setSettings(prev => ({ ...prev, successMessage: e.target.value }))}
              placeholder="Thank you! We will get back to you soon."
            />
          </div>
          <div className="efb-field" style={{ gridColumn: '1 / -1' }}>
            <label className="efb-label">Redirect URL (optional — redirect after submission)</label>
            <input
              className="efb-input"
              value={settings.redirectUrl}
              onChange={(e) => setSettings(prev => ({ ...prev, redirectUrl: e.target.value }))}
              placeholder="https://yourinstitute.com/thank-you"
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="efb-section">
          <div className="efb-section-title"><Eye size={16} /> Preview</div>
          <div className="efb-preview-wrap">
            <div className="efb-preview-frame">
              {formName && <div className="efb-preview-title">{formName}</div>}
              {description && <div className="efb-preview-desc">{description}</div>}
              {fields.map((field) => (
                <div key={field.fieldId} className="efb-preview-group">
                  <label className="efb-preview-label">
                    {field.label}
                    {field.required && <span className="efb-preview-required"> *</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea className="efb-preview-input" placeholder={field.placeholder} rows={3} readOnly />
                  ) : field.type === 'dropdown' ? (
                    <select className="efb-preview-input" disabled>
                      <option>{field.placeholder || 'Select...'}</option>
                      {(field.options || []).map((opt, i) => <option key={i}>{opt}</option>)}
                    </select>
                  ) : field.type === 'radio' ? (
                    <div>{(field.options || []).map((opt, i) => (
                      <label key={i} className="d-flex align-items-center gap-2 mb-1">
                        <input type="radio" disabled /> {opt}
                      </label>
                    ))}</div>
                  ) : field.type === 'checkbox' ? (
                    <div>{(field.options || []).map((opt, i) => (
                      <label key={i} className="d-flex align-items-center gap-2 mb-1">
                        <input type="checkbox" disabled /> {opt}
                      </label>
                    ))}</div>
                  ) : (
                    <input
                      className="efb-preview-input"
                      type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                      placeholder={field.placeholder}
                      readOnly
                    />
                  )}
                </div>
              ))}
              <button className="efb-preview-btn">{settings.submitButtonText || 'Submit'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="efb-footer">
        <AppButton variant="outline" onClick={() => navigate('/enquiry-forms')}>Cancel</AppButton>
        <AppButton onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving...' : (isEdit ? 'Update Form' : 'Create Form')}
        </AppButton>
      </div>
    </div>
  );
};

export default FormBuilder;
